import React, { PropsWithChildren } from "react";

import { useTranslation } from "react-i18next";

import { getActivityIntensityTranslation } from "@/models/diabetes/ActivityModel.ts";
import { getFoodMealTranslation, getFoodSizeTranslation } from "@/models/diabetes/FoodModel.ts";
import { getReportCategoryTranslation } from "@/models/diabetes/ReportModel.ts";
import { SvgDataListTooltip } from "@/uiKit/atoms/svg/SvgDataListTooltip.tsx";
import {
  SvgDataTooltip,
  SvgDataTooltipFooter,
  SvgDataTooltipKeyValueContent,
} from "@/uiKit/atoms/svg/SvgDataTooltip.tsx";
import { Tooltip } from "@/uiKit/atoms/Tooltip.tsx";
import { AdditionalDiabetesInfo } from "@/uiKit/molecules/graphs/AdditionalDiabetesInfoGraph/AdditionalDiabetesInfoUtils.ts";
import { formatNumber } from "@/utils/mathUtils.ts";

export const AdditionalDiabetesInfoTooltip: React.FC<{
  info: AdditionalDiabetesInfo;
}> = ({ info }) => {
  const { t } = useTranslation();

  switch (info.type) {
    case "food": {
      const food = info.data;
      return (
        <SvgDataTooltip>
          <SvgDataTooltipKeyValueContent label={t("food.meal")} value={t(getFoodMealTranslation(food.meal))} />
          {food.carbs ? (
            <SvgDataTooltipKeyValueContent
              label={t("food.carbs")}
              value={`${formatNumber(food.carbs, 1)} ${t("food.carbsUnit")}`}
            />
          ) : (
            <SvgDataTooltipKeyValueContent label={t("food.size")} value={t(getFoodSizeTranslation(food.size))} />
          )}
          {food.comments && <SvgDataTooltipKeyValueContent label={t("food.comments")} value={food.comments} />}
          <SvgDataTooltipFooter date={food.date} inputMethod={food.deviceType} />
        </SvgDataTooltip>
      );
    }
    case "activity": {
      const activity = info.data;
      return (
        <SvgDataTooltip>
          <SvgDataTooltipKeyValueContent
            label={t("activity.intensity")}
            value={t(getActivityIntensityTranslation(activity.intensity))}
          />
          <SvgDataTooltipKeyValueContent
            label={t("activity.duration")}
            value={`${formatNumber(activity.duration, 0)} ${t("activity.durationUnit")}`}
          />
          <SvgDataTooltipFooter date={activity.date} inputMethod={activity.deviceType} />
        </SvgDataTooltip>
      );
    }
    case "report": {
      const report = info.data;
      return (
        <SvgDataTooltip>
          <SvgDataTooltipKeyValueContent
            label={t("report.category")}
            value={t(getReportCategoryTranslation(report.category))}
          />
          <SvgDataTooltipKeyValueContent label={t("report.message")} value={report.message ?? "/"} />
          <SvgDataTooltipFooter date={report.date} inputMethod={report.deviceType} />
        </SvgDataTooltip>
      );
    }
  }
};

type AdditionalDiabetesInfoTooltipContentProps = {
  items: AdditionalDiabetesInfo[];
};

const AdditionalDiabetesInfoTooltipContent: React.FC<AdditionalDiabetesInfoTooltipContentProps> = ({ items }) => {
  return (
    <SvgDataListTooltip>
      {items.map(item => (
        <AdditionalDiabetesInfoTooltip key={item.data.id} info={item} />
      ))}
    </SvgDataListTooltip>
  );
};

export type AdditionalDiabetesInfoPointTooltipProps = PropsWithChildren<{
  items: AdditionalDiabetesInfo[];
  x?: number;
  y?: number;
}>;

export const AdditionalDiabetesInfoPointTooltip: React.FC<AdditionalDiabetesInfoPointTooltipProps> = ({
  items,
  x,
  y,
  children,
}) => (
  <Tooltip
    popupId="additional-diabetes-info-tooltip"
    svg
    placement="top"
    relativeClientPoint
    clientPoint={{
      enabled: true,
      x: x ?? 0,
      y: y ?? 0,
    }}
    content={<AdditionalDiabetesInfoTooltipContent items={items} />}
  >
    {children}
  </Tooltip>
);
